import React, { useState, useEffect, useRef } from 'react';
import { TrendingUp, Clock, Fuel, ShieldCheck, ChevronRight, Info, Send, CheckCircle, Loader2 } from 'lucide-react';
import { trackEvent } from '../lib/analytics';

const API_URL = '';

const ROICalculator = () => {
  const [fleetSize, setFleetSize] = useState(10);
  const [avgMileage, setAvgMileage] = useState(40000);
  const [laborRate, setLaborRate] = useState(65);

  const [results, setResults] = useState({
    fuelSavings: 0,
    laborSavings: 0,
    maintenanceSavings: 0,
    totalAnnualSavings: 0,
    paybackMonths: 0
  });

  // Lead capture state — posts the computed ROI to the same /api/contact
  // endpoint the contact form uses (type: 'ROI Rapport Aanvraag').
  const [lead, setLead] = useState({ name: '', email: '' });
  const [leadStatus, setLeadStatus] = useState('idle'); // idle | loading | done | error

  useEffect(() => {
    const fuelPrice = 1.85;
    const avgConsumption = 0.3;
    const fuelSavingsPct = 0.12;
    const annualAdminSavingPerVehicle = 180;

    const fuelSavings = fleetSize * avgMileage * avgConsumption * fuelSavingsPct * fuelPrice;
    const adminHoursSavedPerVehicle = 8;
    const laborSavings = fleetSize * adminHoursSavedPerVehicle * laborRate;
    const maintenanceSavings = fleetSize * 450;
    const adminSavings = fleetSize * annualAdminSavingPerVehicle;

    const totalAnnualSavings = fuelSavings + laborSavings + maintenanceSavings + adminSavings;

    const upfrontCost = fleetSize * 150;

    setResults({
      fuelSavings: Math.round(fuelSavings),
      laborSavings: Math.round(laborSavings),
      maintenanceSavings: Math.round(maintenanceSavings + adminSavings),
      totalAnnualSavings: Math.round(totalAnnualSavings),
      paybackMonths: Math.max(1, Math.round((upfrontCost / (totalAnnualSavings / 12)) * 10) / 10)
    });
  }, [fleetSize, avgMileage, laborRate]);

  // Fire calculator_result_shown once the first result is computed (P4.5).
  const resultShownRef = useRef(false);
  useEffect(() => {
    if (resultShownRef.current || results.totalAnnualSavings <= 0) return;
    resultShownRef.current = true;
    trackEvent('calculator_result_shown', { fleetSize, annualSavings: results.totalAnnualSavings });
  }, [results.totalAnnualSavings, fleetSize]);

  const fmt = (n) => n.toLocaleString('nl-NL');

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    if (leadStatus === 'loading') return;
    setLeadStatus('loading');

    const message =
      `ROI-rapport aangevraagd via de besparingscalculator.\n` +
      `Vlootgrootte: ${fleetSize} voertuigen\n` +
      `Km/jaar per voertuig: ${fmt(avgMileage)}\n` +
      `Gem. uurtarief personeel: €${laborRate}\n` +
      `— Geschatte jaarlijkse besparing: €${fmt(results.totalAnnualSavings)}\n` +
      `— Brandstof: €${fmt(results.fuelSavings)} | Arbeid: €${fmt(results.laborSavings)} | ` +
      `Onderhoud & admin: €${fmt(results.maintenanceSavings)}\n` +
      `— Terugverdientijd hardware: ${results.paybackMonths} maanden`;

    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: lead.name,
          company: '',
          email: lead.email,
          phone: '',
          fleetSize: String(fleetSize),
          service: 'ROI Calculator',
          message,
          type: 'ROI Rapport Aanvraag',
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || 'Server error');
      }
      setLeadStatus('done');
      trackEvent('roi_lead_submit', { fleetSize, annualSavings: results.totalAnnualSavings });
    } catch (err) {
      console.error('ROI lead submit error:', err);
      setLeadStatus('error');
    }
  };

  const stats = [
    { name: 'Brandstofbesparing', value: `€${fmt(results.fuelSavings)}`, icon: Fuel, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { name: 'Arbeidsefficiëntie', value: `€${fmt(results.laborSavings)}`, icon: Clock, color: 'text-blue-600', bg: 'bg-blue-50' },
    { name: 'Onderhoud & Admin', value: `€${fmt(results.maintenanceSavings)}`, icon: ShieldCheck, color: 'text-purple-600', bg: 'bg-purple-50' },
  ];

  return (
    <section className="py-20 bg-white" id="roi-calculator">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <span className="bg-blue-100 text-blue-700 px-4 py-1.5 rounded-full text-sm font-bold tracking-wider uppercase mb-4 inline-block">
            ROI Calculator
          </span>
          <h2 className="text-4xl lg:text-5xl font-extrabold text-slate-900 mb-6">
            Bereken uw <span className="text-blue-600">jaarlijkse besparing</span>
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Ontdek hoeveel u kunt besparen op brandstof, arbeid en onderhoud door de implementatie van FleetTrack.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 bg-slate-50 p-8 lg:p-10 rounded-[2.5rem] border border-slate-100 shadow-xl shadow-slate-200/50">
            <div className="space-y-10">
              <div>
                <div className="flex justify-between items-center mb-4">
                  <label className="text-slate-900 font-bold flex items-center">
                    Aantal voertuigen
                    <div className="ml-2 bg-white p-1 rounded-md shadow-sm border border-slate-200 group relative">
                      <Info className="h-4 w-4 text-slate-400" />
                      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 p-2 bg-slate-900 text-white text-[10px] rounded pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity z-10">
                        Inclusief vrachtwagens, bestelwagens en personenauto's.
                      </div>
                    </div>
                  </label>
                  <input type="number" inputMode="numeric" min="1" step="1" value={fleetSize} onChange={(e) => setFleetSize(Math.max(1, parseInt(e.target.value, 10) || 1))} aria-label="Aantal voertuigen" className="w-24 px-2 py-1 bg-slate-50 border border-slate-200 rounded-lg text-right text-blue-600 font-black text-2xl" />
                </div>
                <input type="range" aria-label="Aantal voertuigen" min="1" max="250" value={fleetSize} onChange={(e) => setFleetSize(parseInt(e.target.value))} className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600" />
              </div>

              <div>
                <div className="flex justify-between items-center mb-4">
                  <label className="text-slate-900 font-bold">Km per jaar / voertuig</label>
                  <span className="text-blue-600 font-black text-2xl">{avgMileage.toLocaleString()} <small className="text-sm font-bold text-slate-500">km</small></span>
                </div>
                <input type="range" aria-label="Kilometers per jaar per voertuig" min="5000" max="150000" step="5000" value={avgMileage} onChange={(e) => setAvgMileage(parseInt(e.target.value))} className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600" />
              </div>

              <div>
                <div className="flex justify-between items-center mb-4">
                  <label className="text-slate-900 font-bold">Gem. uurtarief personeel</label>
                  <span className="text-blue-600 font-black text-2xl">€{laborRate} <small className="text-sm font-bold text-slate-500">/uur</small></span>
                </div>
                <input type="range" aria-label="Gemiddeld uurtarief personeel in euro" min="20" max="120" value={laborRate} onChange={(e) => setLaborRate(parseInt(e.target.value))} className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-600" />
              </div>

              <div className="pt-8 border-t border-slate-200">
                <div className="flex items-center p-4 bg-white rounded-2xl border border-blue-50">
                  <TrendingUp className="h-10 w-10 text-blue-600 mr-4" />
                  <div>
                    <p className="text-sm text-slate-500 font-medium">Terugverdientijd hardware</p>
                    <p className="text-2xl font-black text-slate-900">{results.paybackMonths} maanden</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-slate-900 rounded-[3rem] p-8 lg:px-12 lg:py-16 text-white relative overflow-hidden shadow-2xl shadow-blue-200">
              <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 -mr-32 -mt-32 animate-pulse"></div>

              <div className="relative z-10">
                <p className="text-blue-400 font-bold uppercase tracking-widest text-sm mb-4">Uw geschatte jaarlijkse besparing</p>
                <div className="text-6xl lg:text-8xl font-black mb-12 tracking-tighter">
                  €{fmt(results.totalAnnualSavings)}
                  <span className="text-2xl font-bold text-slate-500 ml-4 tracking-normal">per jaar</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
                  {stats.map((stat) => (
                    <div key={stat.name} className="bg-white/5 backdrop-blur-sm border border-white/10 p-6 rounded-3xl group hover:bg-white transition-colors duration-500">
                      <stat.icon className={`h-8 w-8 mb-4 transition-colors ${stat.color} group-hover:text-blue-600`} />
                      <p className="text-slate-400 text-xs font-bold uppercase tracking-wider mb-1 group-hover:text-slate-500">{stat.name}</p>
                      <p className="text-xl font-black text-white group-hover:text-slate-900 transition-colors">{stat.value}</p>
                    </div>
                  ))}
                </div>

                {/* Lead capture — email the full ROI report */}
                {leadStatus === 'done' ? (
                  <div className="bg-emerald-500/15 border border-emerald-400/30 rounded-3xl p-6 flex items-start gap-4">
                    <CheckCircle className="h-7 w-7 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-black text-lg">Bedankt! Uw ROI-rapport is onderweg.</p>
                      <p className="text-slate-400 text-sm mt-1">
                        We sturen uw persoonlijke besparingsoverzicht naar {lead.email}. Geen e-mail ontvangen? Check uw spam of mail naar info@fleettrackholland.nl.
                      </p>
                    </div>
                  </div>
                ) : (
                  <form onSubmit={handleLeadSubmit} className="bg-white/5 border border-white/10 rounded-3xl p-6 mb-6">
                    <p className="font-bold mb-1">Ontvang dit ROI-rapport gratis per e-mail</p>
                    <p className="text-slate-400 text-sm mb-4">Inclusief besparingsoverzicht, terugverdientijd en een vrijblijvend prijsvoorstel.</p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <input
                        type="text"
                        required
                        value={lead.name}
                        onChange={(e) => setLead((p) => ({ ...p, name: e.target.value }))}
                        placeholder="Uw naam"
                        className="flex-1 bg-white/10 border border-white/15 rounded-2xl px-4 py-3.5 text-white placeholder-slate-500 focus:outline-none focus:border-blue-400 transition-colors"
                      />
                      <input
                        type="email"
                        required
                        value={lead.email}
                        onChange={(e) => setLead((p) => ({ ...p, email: e.target.value }))}
                        placeholder="Uw e-mailadres"
                        className="flex-1 bg-white/10 border border-white/15 rounded-2xl px-4 py-3.5 text-white placeholder-slate-500 focus:outline-none focus:border-blue-400 transition-colors"
                      />
                    </div>
                    {leadStatus === 'error' && (
                      <p className="text-red-400 text-sm mt-3">
                        Er ging iets mis. Mail naar <a href="mailto:info@fleettrackholland.nl" className="underline">info@fleettrackholland.nl</a> of probeer opnieuw.
                      </p>
                    )}
                    <button
                      type="submit"
                      disabled={leadStatus === 'loading'}
                      className="mt-4 w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-60 px-8 py-4 rounded-2xl font-black text-center transition-all active:scale-95 shadow-lg shadow-blue-500/20 flex items-center justify-center"
                    >
                      {leadStatus === 'loading' ? (
                        <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Versturen…</>
                      ) : (
                        <>Ontvang gratis ROI rapport <ChevronRight className="ml-2 h-5 w-5" /></>
                      )}
                    </button>
                  </form>
                )}

                <div className="flex flex-col sm:flex-row gap-4">
                  <a
                    href="/contact/"
                    onClick={() => trackEvent('cta_click', { cta_text: 'Spreek een expert', cta_location: 'roi', roi_calculated: true })}
                    className="flex-1 bg-white/10 hover:bg-white/20 px-8 py-4 rounded-2xl font-bold text-center transition-all active:scale-95 backdrop-blur-md border border-white/10 flex items-center justify-center"
                  >
                    <Send className="mr-2 h-5 w-5" /> Spreek een expert
                  </a>
                </div>

                <p className="mt-8 text-slate-400 text-xs leading-relaxed italic">
                  * Deze berekening is een schatting op basis van gemiddelde industriestandaarden. Werkelijke resultaten kunnen variëren afhankelijk van uw specifieke bedrijfsvoering, voertuigtypen en rijomstandigheden.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ROICalculator;
